import { Model } from './model';
import { Type } from 'class-transformer';
import { Attachment } from './attachment';
import { Claim } from './claim';
import { DeductionDocument } from './deduction_document';
import { DeductionCreditMemo } from './deduction_credit_memo';
import { DeductionReason } from './deduction_reason';
import { Partner } from './partner';
import { TransformStringToDayJS } from './utils';
import dayjs from 'dayjs';
import type { ApprovalStatus } from './claim_line_item';

export class RelatedInvoice {
  id: string;
  number: string;
  amount_due: number;
  amount_paid: number;
  currency: string;

  @TransformStringToDayJS()
  due_date: dayjs.Dayjs | null;

  @TransformStringToDayJS()
  issued_at: dayjs.Dayjs | null;

  purchase_order_number: string | null;
  status: string;
}

export enum DeductionStatus {
  PRE_DEDUCTION = 'pre_deduction',
  OPEN = 'open',
  RESEARCH = 'research',
  ARCHIVED = 'archived',
  CLOSED = 'closed',
  UNMATCHED = 'unmatched',
}

const StatusVariant = {
  archived: 'secondary',
  closed: 'default',
  open: 'open',
  pre_deduction: 'outline',
  research: 'warning',
  unmatched: 'outline',
} as const;

export class Deduction extends Model {
  id: string;

  amount: number;

  currency: string;

  disputed_invoice_number: string | null;

  external_reason: string | null;

  external_reason_code: string | null;

  notes: string | null;

  purchase_order_number: string | null;

  purchase_order_numbers: string[];

  deal_numbers: string[];

  invoices: string[];

  @Type(() => RelatedInvoice)
  related_invoices: RelatedInvoice[];

  reason_id: string;

  is_pre_deduction: boolean;

  is_processing: boolean;

  has_duplicates: boolean;

  has_alerts: boolean;

  alerts: string[] | null;

  has_new_changes: boolean;

  is_new: boolean;

  @Type(() => Deduction)
  duplicates: Deduction[];

  reference_number: string | null;

  short_paid_invoice_amount: number | null;

  short_paid_invoice_number: string | null;

  status: DeductionStatus | null;

  type: string | null;

  @TransformStringToDayJS()
  received_at: dayjs.Dayjs | null;

  @TransformStringToDayJS()
  payment_date: dayjs.Dayjs | null;

  @TransformStringToDayJS()
  effective_payment_date: dayjs.Dayjs | null;

  @TransformStringToDayJS()
  issue_date: dayjs.Dayjs | null;

  @TransformStringToDayJS()
  effective_issue_date: dayjs.Dayjs | null;

  @TransformStringToDayJS()
  claim_issue_date: dayjs.Dayjs | null;

  @TransformStringToDayJS()
  effective_claim_issue_date: dayjs.Dayjs | null;

  @TransformStringToDayJS()
  purchase_order_date: dayjs.Dayjs | null;

  @TransformStringToDayJS()
  short_paid_invoice_date: dayjs.Dayjs | null;

  @Type(() => Claim)
  claims: Claim[];

  @Type(() => Claim)
  aggregated_claim: Claim | null;

  @Type(() => DeductionCreditMemo)
  credit_memos: DeductionCreditMemo[];

  @Type(() => DeductionDocument)
  document: DeductionDocument | null;

  @Type(() => Partner)
  partner: Partner | null;

  @Type(() => DeductionReason)
  reason: DeductionReason | null;

  valid_status_transitions: DeductionStatus[];

  meta: Record<string, any> | null;

  approval_status: ApprovalStatus;

  get approved() {
    return this.approval_status === 'approved';
  }

  get rejected() {
    return this.approval_status === 'rejected';
  }

  get pending() {
    return this.approval_status === 'pending';
  }

  get line_items() {
    // Use aggregated_claim if it exists, otherwise fall back to finding the first claim with line items
    if (this.aggregated_claim) {
      return this.aggregated_claim.line_items || [];
    }

    const claimWithLineItems = this.claims.find(
      (claim) => claim.line_items && claim.line_items.length > 0,
    );
    return claimWithLineItems?.line_items || [];
  }

  get line_items_sum() {
    // Use aggregated_claim if it exists, otherwise fall back to finding the first claim with line items
    if (this.aggregated_claim) {
      return this.aggregated_claim.line_items_sum || 0;
    }

    const claimWithLineItems = this.claims.find(
      (claim) => claim.line_items && claim.line_items.length > 0,
    );
    return claimWithLineItems?.line_items_sum || 0;
  }

  get amount_diff() {
    return Number(this.amount) - this.line_items_sum;
  }

  get amount_mismatch() {
    return Math.round(this.line_items_sum * 100) !== Math.round(Number(this.amount) * 100);
  }

  // get claim_conflict() {
  //   return (
  //     Number(this.claim?.amount) &&
  //     Number(this.amount) !== Number(this.claim?.amount)
  //   );
  // }

  // get claim_conflict_amount() {
  //   return Number(this.amount) - Number(this.claim?.amount);
  // }

  get statusVariant() {
    return this.status && this.status in StatusVariant
      ? StatusVariant[this.status as keyof typeof StatusVariant]
      : 'default';
  }
}
